import { Li, Span, Text, Ul } from "@clipboard-health/ui-react";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { logEvent } from "@src/appV2/lib/analytics";
import { STATE_LICENSE_REQUIREMENT_ID, USER_EVENTS } from "@src/constants/index";
import pluralize from "pluralize";
import { useMemo } from "react";

import { MISSING_DOCS_ALERT_INFO_MESSAGES } from "./constants";
import { CbhDocument, ShiftItemCategory } from "../model";

interface MissingDocumentsItemProps {
  isOpen: boolean;
  onDidDismiss: () => void;
  onConfirm: () => void;
  missingDocuments: Set<CbhDocument>;
  category: ShiftItemCategory;
  expiringInFuture: boolean;
}
export function MissingDocumentsAlert(props: MissingDocumentsItemProps) {
  const { isOpen, onDidDismiss, onConfirm, missingDocuments, expiringInFuture } = props;

  const { visibleDocumentsCount, missingDocsInfo, hasMoreDocuments } = useMemo(() => {
    const isPlural = missingDocuments.size > 1;
    const hasMoreDocuments = missingDocuments.size > 6;

    const visibleDocs: CbhDocument[] = [];
    const invisibleDocs: CbhDocument[] = [];

    for (const requirement of missingDocuments) {
      if (requirement.reqId === STATE_LICENSE_REQUIREMENT_ID) {
        visibleDocs.push({ ...requirement, name: "State License (upload as Miscellaneous)" });
      } else if (requirement.visibleToHCP) {
        visibleDocs.push(requirement);
      } else {
        invisibleDocs.push(requirement);
      }
    }

    return {
      visibleDocumentsCount: visibleDocs.length,
      missingDocsInfo: { visibleDocs, invisibleDocs, isPlural, expiringInFuture },
      hasMoreDocuments,
    };
  }, [expiringInFuture, missingDocuments]);

  function determineVisibleDocsMessage() {
    if (expiringInFuture) {
      return missingDocsInfo.visibleDocs.length > 1
        ? MISSING_DOCS_ALERT_INFO_MESSAGES.MULTIPLE_VISIBLE_DOCS_MISSING_IN_FUTURE_MESSAGE
        : MISSING_DOCS_ALERT_INFO_MESSAGES.SINGLE_VISIBLE_DOC_MISSING_IN_FUTURE_MESSAGE;
    } else {
      return missingDocsInfo.isPlural
        ? MISSING_DOCS_ALERT_INFO_MESSAGES.MULTIPLE_DOCS_MISSING_CURRENTLY_MESSAGE
        : MISSING_DOCS_ALERT_INFO_MESSAGES.SINGLE_DOC_MISSING_CURRENTLY_MESSAGE;
    }
  }

  function determineInvisibleDocsMessage() {
    let invisibleDocsMessage = "";

    const visibleDocsCount = missingDocsInfo.visibleDocs.length;
    const { invisibleDocs } = missingDocsInfo;

    if (expiringInFuture) {
      if (visibleDocsCount && invisibleDocs.length) {
        invisibleDocsMessage =
          invisibleDocs.length > 1
            ? MISSING_DOCS_ALERT_INFO_MESSAGES.MULTIPLE_INVISIBLE_DOCS_MISSING_IN_FUTURE_MESSAGE
            : MISSING_DOCS_ALERT_INFO_MESSAGES.SINGLE_INVISIBLE_DOC_MISSING_IN_FUTURE_MESSAGE;
      } else if (invisibleDocs.length) {
        invisibleDocsMessage =
          invisibleDocs.length > 1
            ? MISSING_DOCS_ALERT_INFO_MESSAGES.ONLY_MULTIPLE_INVISIBLE_DOCS_MISSING_IN_FUTURE_MESSAGE
            : MISSING_DOCS_ALERT_INFO_MESSAGES.ONLY_SINGLE_INVISIBLE_DOC_MISSING_IN_FUTURE_MESSAGE;
      }
    } else if (invisibleDocs.length > 0) {
      invisibleDocsMessage =
        MISSING_DOCS_ALERT_INFO_MESSAGES.MESSAGE_FOR_INVISIBLE_DOCS_MISSING_CURRENTLY;
    }

    return invisibleDocsMessage;
  }

  return (
    <Dialog open={isOpen} onClose={onDidDismiss}>
      <DialogTitle sx={{ textAlign: "center" }}>
        <AddCircleIcon color={"primary"} fontSize="large" />
        <br />
        You're Missing{" "}
        <Span color={(theme) => theme.palette.primary.main}>{missingDocuments.size}</Span>{" "}
        {pluralize("document", missingDocuments.size)}
      </DialogTitle>

      <DialogContent data-testid="missing-docs-msg">
        <DialogContentText>
          {missingDocsInfo.visibleDocs.length > 0 && (
            <>
              <Text>{determineVisibleDocsMessage()}</Text>
              <br />
              <Ul>
                {missingDocsInfo.visibleDocs.slice(0, 6).map((document) => (
                  <Li key={document.reqId}>
                    <Text bold>{document.name}</Text>
                  </Li>
                ))}
              </Ul>
            </>
          )}
          {missingDocsInfo.invisibleDocs.length > 0 && missingDocsInfo.visibleDocs.length < 6 && (
            <>
              <Text>{determineInvisibleDocsMessage()}</Text>
              <br />
              <Ul>
                {missingDocsInfo.invisibleDocs
                  .slice(0, 6 - missingDocsInfo.visibleDocs.length)
                  .map((document) => (
                    <Li key={document.reqId}>
                      <Text bold>{document.name}</Text>
                    </Li>
                  ))}
              </Ul>
            </>
          )}
          {hasMoreDocuments && (
            <Text>Click to upload your documents and view the remaining missing requirements.</Text>
          )}
        </DialogContentText>
      </DialogContent>

      <DialogActions>
        <Button
          variant="outlined"
          onClick={() => {
            onDidDismiss();
            logEvent(USER_EVENTS.TAPPED_CANCEL_MISSING_DOCS_POPUP_BUTTON);
          }}
        >
          {visibleDocumentsCount ? "Cancel" : "Okay"}
        </Button>
        {visibleDocumentsCount > 0 && (
          <Button onClick={onConfirm} variant="contained">
            Upload Docs
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
